import { useState } from "react";

/**
 * Minimal SVG line chart (no chart library): several series over shared x
 * labels, gaps where a value is null, optional shaded bands (downturns), and a
 * hover read-out. Sized to its container width via viewBox. `right` puts
 * extra series on their own scale at the right edge (economy overlays on Trends).
 */
export interface Series { label: string; color: string; points: Array<number | null>; dashed?: boolean }

function niceScale(vals: number[]) {
  let lo = Math.min(...vals), hi = Math.max(...vals);
  if (lo === hi) { lo -= 1; hi += 1; }
  // "Nice" ticks: a 1 / 2 / 2.5 / 5 × 10^n step, axis snapped to it.
  const raw = (hi - lo) / 4, mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * mag).find((st) => st >= raw)!;
  lo = Math.floor(lo / step) * step; hi = Math.ceil(hi / step) * step;
  return { lo, hi, ticks: Array.from({ length: Math.round((hi - lo) / step) + 1 }, (_, k) => lo + k * step) };
}

export function LineChart({ x, series, format, height = 260, bands = [], tickEvery, right }: {
  x: string[]; series: Series[]; format: (v: number) => string; height?: number;
  bands?: Array<{ from: string; to: string; label?: string; color?: string; opacity?: number }>; tickEvery?: number;
  right?: { series: Series[]; format: (v: number) => string };
}) {
  const rVals = (right?.series ?? []).flatMap((s) => s.points.filter((v): v is number => v !== null && Number.isFinite(v)));
  const W = 900, H = height, L = 64, R = rVals.length ? 56 : 12, T = 12, B = 28;
  const [hover, setHover] = useState<number | null>(null);
  const vals = series.flatMap((s) => s.points.filter((v): v is number => v !== null && Number.isFinite(v)));
  if (!x.length || !vals.length) return <p className="muted">No data for this selection.</p>;
  const { lo, hi, ticks: yTicks } = niceScale(vals);
  const rs = rVals.length ? niceScale(rVals) : null;
  const pyR = (v: number) => (rs ? T + ((rs.hi - v) * (H - T - B)) / (rs.hi - rs.lo) : 0);
  const px = (i: number) => L + (x.length === 1 ? (W - L - R) / 2 : (i * (W - L - R)) / (x.length - 1));
  const py = (v: number) => T + ((hi - v) * (H - T - B)) / (hi - lo);
  const step = tickEvery ?? Math.max(1, Math.ceil(x.length / 10));

  const path = (pts: Array<number | null>, yf = py) => {
    let d = "", pen = false;
    pts.forEach((v, i) => {
      if (v === null || !Number.isFinite(v)) { pen = false; return; }
      d += `${pen ? "L" : "M"}${px(i).toFixed(1)},${yf(v).toFixed(1)}`; pen = true;
    });
    return d;
  };
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const xx = ((e.clientX - r.left) / r.width) * W;
    const i = Math.round(((xx - L) / (W - L - R)) * (x.length - 1));
    setHover(i >= 0 && i < x.length ? i : null);
  };

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ display: "block" }}>
        {bands.map((b, k) => {
          const i0 = x.findIndex((m) => m >= b.from), i1 = x.findIndex((m) => m >= b.to);
          if (i0 < 0) return null;
          const a = px(i0), z = px(i1 < 0 ? x.length - 1 : i1);
          return <rect key={k} x={a} y={T} width={Math.max(z - a, 2)} height={H - T - B} fill={b.color ?? "#b42318"} opacity={b.opacity ?? 0.08} />;
        })}
        {yTicks.map((v, k) => (
          <g key={k}>
            <line x1={L} x2={W - R} y1={py(v)} y2={py(v)} stroke="#e5e7eb" />
            <text x={L - 6} y={py(v) + 4} fontSize={11} textAnchor="end" fill="#6b7280">{format(v)}</text>
          </g>
        ))}
        {rs && rs.ticks.map((v, k) => (
          <text key={`r${k}`} x={W - R + 6} y={pyR(v) + 4} fontSize={11} textAnchor="start" fill="#8f6b2a">{right!.format(v)}</text>
        ))}
        {x.map((lab, i) => (i % step === 0 ? <text key={i} x={px(i)} y={H - 8} fontSize={11} textAnchor="middle" fill="#6b7280">{lab}</text> : null))}
        {series.map((s) => <path key={s.label} d={path(s.points)} fill="none" stroke={s.color} strokeWidth={2} strokeDasharray={s.dashed ? "6 4" : undefined} />)}
        {rs && right!.series.map((s) => <path key={`r-${s.label}`} d={path(s.points, pyR)} fill="none" stroke={s.color} strokeWidth={1.5} strokeDasharray={s.dashed ? "6 4" : "2 3"} />)}
        {hover !== null && <line x1={px(hover)} x2={px(hover)} y1={T} y2={H - B} stroke="#15263d" strokeDasharray="3 3" />}
        {hover !== null && series.map((s) => s.points[hover] != null && (
          <circle key={s.label} cx={px(hover)} cy={py(s.points[hover]!)} r={3.5} fill={s.color} />
        ))}
      </svg>
      {hover !== null && (
        <div className="chart-tip" style={{ left: `${Math.min(80, (px(hover) / W) * 100)}%` }}>
          <strong>{x[hover]}</strong>
          {series.map((s) => <div key={s.label}><span style={{ color: s.color }}>●</span> {s.label}: {s.points[hover] == null ? "—" : format(s.points[hover]!)}</div>)}
          {rs && right!.series.map((s) => <div key={`r-${s.label}`}><span style={{ color: s.color }}>●</span> {s.label}: {s.points[hover] == null ? "—" : right!.format(s.points[hover]!)}</div>)}
        </div>
      )}
      {(series.length > 1 || !!rs) && (
        <div className="row" style={{ gap: 14, fontSize: 12, marginTop: 4 }}>
          {series.map((s) => <span key={s.label}><span style={{ color: s.color }}>●</span> {s.label}</span>)}
          {rs && right!.series.map((s) => <span key={`r-${s.label}`}><span style={{ color: s.color }}>●</span> {s.label} <span className="muted">(right scale)</span></span>)}
        </div>
      )}
    </div>
  );
}

/** Tiny line for indicator cards. */
export function Sparkline({ points, color = "#15263d" }: { points: number[]; color?: string }) {
  if (points.length < 2) return null;
  const W = 160, H = 36, lo = Math.min(...points), hi = Math.max(...points), span = hi - lo || 1;
  const d = points.map((v, i) => `${i ? "L" : "M"}${((i * W) / (points.length - 1)).toFixed(1)},${(H - 2 - ((v - lo) * (H - 4)) / span).toFixed(1)}`).join("");
  return <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H}><path d={d} fill="none" stroke={color} strokeWidth={1.5} /></svg>;
}
